'use client';

import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { Add, Visibility, Delete, Block, CheckCircle } from '@mui/icons-material';
import AddCategoryDrawer from './AddCategoryDrawer';
import ViewCategoryDrawer from './ViewCategoryDrawer';
import axiosClient from '../../api/axiosClient';

interface Advantage {
  id?: number;
  type: string;
  yearlyLimit: number;
  companyId?: number;
  version?: number;
}

interface Category {
  id: number;
  name: string;
  status: string; 
  isStandard: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  advantages: Advantage[];
}

export const ADVANTAGE_TYPE = {
  gift: 'GIFT',
  restaurant: 'RESTAURANT',
  culture: 'CULTURE',
  sport: 'SPORT',
  holidays: 'HOLIDAYS',
  help: 'HELP',
  mobility: 'MOBILITY',
  remote: 'REMOTE',
};

export default function CategoriesView() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [viewCategoryId, setViewCategoryId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);


  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);


  const [deactivatingId, setDeactivatingId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosClient.get('/categories', {
        params: { page: page + 1, pageSize: rowsPerPage },
      });
      setCategories(res.data.categories || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  
  const handleOpenDeleteDialog = (categoryId: number) => {
    setDeleteCategoryId(categoryId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteCategoryId) return;
    setDeleting(true);
    try {
      await axiosClient.delete(`/categories/${deleteCategoryId}`);
      setDeleteDialogOpen(false);
      fetchCategories();
    } catch (err: any) {
      console.error('Erreur suppression:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteCategoryId(null);
    }
  };


  const handleDeactivate = async (categoryId: number, currentStatus: string) => {
    setDeactivatingId(categoryId);
    try {
      const newAction = currentStatus === 'inactive' ? 'activate' : 'deactivate';
      const res = await axiosClient.put(`/categories/${categoryId}/status`, {
        action: newAction,
      });
      if (res.data.result?.success) {

        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === categoryId ? { ...cat, status: newAction === 'deactivate' ? 'inactive' : 'active' } : cat
          )
        );
      }
    } catch (err: any) {
      console.error('Erreur désactivation:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Erreur lors de la désactivation');
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" sx={{ color: '#714291', mb: 3 }}>
        Categories Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Stack alignItems="center" sx={{ my: 2 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      <Stack direction="row" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowAddDrawer(true)}
          sx={{
            backgroundColor: '#714291',
            '&:hover': { backgroundColor: '#5a336f' },
            textTransform: 'none',
          }}
        >
          Add Category
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#714291' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Name</TableCell>
              <TableCell sx={{ color: '#fff' }}>Advantages</TableCell>
              <TableCell sx={{ color: '#fff' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff' }}>Total </TableCell>
              <TableCell sx={{ color: '#fff', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {categories.map((cat) => (
              <TableRow
                key={cat.id}
                hover
                sx={{
                  backgroundColor: cat.status === 'inactive' ? '#f5f5f5' : 'inherit',
                  opacity: cat.status === 'inactive' ? 0.6 : 1,
                }}
              >
                <TableCell>{cat.name}</TableCell>
                <TableCell>
                  {cat.advantages.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {cat.advantages.map((adv) => (
                        <li key={adv.id || adv.type}>
                          {adv.type} – {adv.yearlyLimit}€
                        </li>
                      ))}
                    </ul>
                  ) : (
                    'No advantages'
                  )}
                </TableCell>
                <TableCell>
                  {cat.status === 'inactive' ? (
                    <Typography color="error">Inactive</Typography>
                  ) : (
                    <Typography color="success.main">Active</Typography>
                  )}
                </TableCell>
              <TableCell>
  {cat.advantages.length > 0
    ? cat.advantages.reduce((sum, adv) => sum + (adv.yearlyLimit || 0), 0)
    : 0} €
</TableCell>


                <TableCell align="center">
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      setShowAddDrawer(true);
                      setEditingCategory(cat);
                    }}
                    disabled={cat.status === 'inactive'}
                  >
                    Edit
                  </Button>

                  <IconButton
                    color="primary"
                    onClick={() => setViewCategoryId(String(cat.id))}
                    sx={{ ml: 1 }}
                  >
                    <Visibility />
                  </IconButton>

                  <IconButton
                    color="error"
                    onClick={() => handleOpenDeleteDialog(cat.id)}
                    sx={{ ml: 1 }}
                  >
                    <Delete />
                  </IconButton>

                  <Tooltip
                    title={
                      cat.status === 'inactive'
                        ? 'Activate Category'
                        : 'Deactivate Category'
                    }
                  >
                    <span>
                      <IconButton
                        color={cat.status === 'inactive' ? 'success' : 'warning'}
                        onClick={() => handleDeactivate(cat.id, cat.status)}
                        sx={{ ml: 1 }}
                        disabled={deactivatingId === cat.id}
                      >
                        {deactivatingId === cat.id ? (
                          <CircularProgress size={20} />
                        ) : cat.status === 'inactive' ? (
                          <CheckCircle />
                        ) : (
                          <Block />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!loading && categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Aucune catégorie trouvée
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 20]}
      />

  
      <AddCategoryDrawer
        open={showAddDrawer}
        onClose={() => {
          setShowAddDrawer(false);
          setEditingCategory(null);
        }}
        onSuccess={fetchCategories}
        editingCategory={editingCategory}
      />

 
      {viewCategoryId && (
        <ViewCategoryDrawer
          open={Boolean(viewCategoryId)}
          categoryId={viewCategoryId}
          onClose={() => setViewCategoryId(null)}
        />
      )}

  
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { p: 3 } }}
      >
        <DialogTitle>Confirmation de suppression</DialogTitle>
        <DialogContent>
          <Typography>Voulez-vous vraiment supprimer cette catégorie ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
